/* storybook-check-ignore */
import React from 'react';
import { Box, Flex, Button, BoxProps } from '@opendoor/bricks/core';
import { useObservability } from '../../helpers/observability';
import { getFaqItems } from '../../helpers/exclusives/faq';
import Accordion from './Accordion';
import { css } from '@emotion/react';

const opendoorOwnedFaqItems = getFaqItems([
  'what-are-opendoor-exclusives',
  'how-does-the-process-work',
  'how-is-buying-an-opendoor-exclusive-different-from-the-typical-buying-experience',
  'what-is-the-appraisal-price-match-guarantee',
  'what-if-i-go-into-contract-and-change-my-mind',
  'how-does-pricing-work-can-i-negotiate-the-price',
  'if-im-working-with-an-agent-how-are-they-compensated',
  'should-i-get-the-home-inspected',
]);

const customerOwnedFaqItems = getFaqItems([
  'what-is-a-customer-owned-exclusive',
  'how-does-the-process-work-customer-owned',
  'how-does-the-appraisal-price-match-guarantee-apply-to-customer-owned-exclusives',
  'what-is-exclusives-savings-and-why-do-i-pay-less',
  'do-i-need-to-work-with-an-agent',
  'can-i-choose-my-closing-date-for-customer-owned-exclusives',
  'do-i-need-a-pre-approval-to-submit-an-offer-for-a-customer-owned-exclusive',
  'can-i-submit-an-offer-for-more-or-less-than-the-asking-price',
  'am-i-guaranteed-to-get-the-home-if-i-submit-a-contract-for-a-customer-owned-exclusive',
]);

const publicOpendoorOwnedFaqItems = getFaqItems([
  'what-homes-are-eligible-for-purchases-on-exclusives',
  'what-benefits-are-associated-with-buying-a-publicly-listed-opendoor-owned-home-on-exclusives',
  'how-does-the-exclusives-process-work-for-publicly-owned-homes',
  'can-i-negotiate-the-price-of-the-home',
  'what-if-there-area-already-offer-on-the-omls-that-im-looking',
  'can-i-find-listed-opendoor-owned-homes-on-the-opendoor-app-or-mls',
  'do-i-need-to-work-with-an-agent',
  'why-do-i-pay-less-when-i-buy-a-publicly-listed-opendoor-owned-home-via-exclusives',
  'what-if-the-home-appraises-for-less',
  'do-i-need-to-use-your-partner-lender-lower-to-purchase-an-opendoor-exclusive',
  'what-kind-of-contract-will-i-sign',
  'how-can-i-tour-a-publicly-listed-opendoor-owned-home',
  'can-i-use-an-fha-loan-to-finance-the-purchase-of-a-publicly-listed-opendoor-owned-home',
  'can-i-use-a-va-loan-to-finance-the-purchase-of-a-publicly-listed-opendoor-owned-home',
  'should-i-get-my-publicly-listed-opendoor-owned-home-inspected',
  'what-is-the-appraisal-price-match-guarantee',
  'if-im-working-with-an-agent-how-are-they-compensated',
]);

const FAQFooter: React.FC<
  { isCustomerOwned: boolean; showOwnedInfo?: boolean; isOOMLS?: boolean } & BoxProps
> = ({ isCustomerOwned, isOOMLS, showOwnedInfo, ...props }) => {
  const { trackEvent } = useObservability();
  return (
    <Box className="exclusive-faq">
      <Flex
        pt="1.5rem"
        pb={['1rem', null, null, '3rem']}
        fontWeight="bold"
        fontSize={['s4', null, null, null, 's3']}
        {...props}
      >
        FAQ{' '}
        {showOwnedInfo && (
          <>
            - {isCustomerOwned ? 'Customer' : isOOMLS ? 'Publicly Listed Opendoor' : 'Opendoor'}{' '}
            Owned Exclusive
          </>
        )}
      </Flex>
      <Accordion
        noLeftPadding
        accordionName="faqFooterAccordion"
        items={
          isCustomerOwned
            ? customerOwnedFaqItems
            : isOOMLS
            ? publicOpendoorOwnedFaqItems
            : opendoorOwnedFaqItems
        }
      />
      <Box pt="1rem">
        <Button
          aria-label=""
          width="auto"
          variant="secondary"
          className="buy-button"
          onClick={() => {
            // TODO: remove categoryOverride
            // observability is initialized with 'cosmos' as the category for all events
            // NOTE: this is also tracked in Journey as `cosmos-exclusives-FAQ-footer`
            trackEvent('cta-click', 'faq-see-all', undefined, {
              categoryOverride: 'exclusive-listings',
            });
            window.location.assign(`/exclusives/faq`);
          }}
          analyticsName="cosmos-exclusives-FAQ-footer"
          {...(showOwnedInfo && {
            width: '100%',
            color: 'brand50',
            borderColor: 'brand50',
            css: css`
              &:hover[variant='secondary'] {
                opacity: 0.8;
                background-color: #faf5f5;
                color: var(--colors-brand50);
              }
            `,
          })}
        >
          See all FAQ
        </Button>
      </Box>
    </Box>
  );
};

export default FAQFooter;
